/**
 * @file GPU-instanced particle effects (GDD section 15).
 *
 * Every particle in the game lives in one `InstancedMesh`, so muzzle flashes,
 * impacts and death bursts together cost a single draw call. State is kept in
 * flat typed arrays and compacted with swap-remove, so the system allocates
 * nothing after construction.
 *
 * Emission is driven purely by events: the system never asks anyone for state.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import { Transform, Destructible } from '../components/index.js';
import { Events } from '../core/EventBus.js';
import { randomRange } from '../utils/MathUtils.js';
import { PALETTE } from '../config/Palette.js';
import { ENEMIES } from '../config/Enemies.js';
import { WAVES } from '../config/Waves.js';

export class ParticleSystem extends System {
  static priority = 90;

  constructor() {
    super();
    /** @type {THREE.InstancedMesh | null} @private */
    this._mesh = null;
    /** @type {number} @private */
    this._capacity = 0;
    /** @type {number} Live particle count; also the instance draw count. @private */
    this._count = 0;

    /** @type {Float32Array | null} @private */ this._positions = null;
    /** @type {Float32Array | null} @private */ this._velocities = null;
    /** @type {Float32Array | null} @private */ this._life = null;
    /** @type {Float32Array | null} @private */ this._maxLife = null;
    /** @type {Float32Array | null} @private */ this._size = null;
    /** @type {Float32Array | null} @private */ this._colors = null;

    /** @type {THREE.Matrix4} @private */ this._matrix = new THREE.Matrix4();
    /** @type {THREE.Vector3} @private */ this._position = new THREE.Vector3();
    /** @type {THREE.Quaternion} @private */ this._quaternion = new THREE.Quaternion();
    /** @type {THREE.Vector3} @private */ this._scale = new THREE.Vector3();
    /** @type {THREE.Color} @private */ this._color = new THREE.Color();
    /** @type {(() => void)[]} @private */ this._subscriptions = [];
  }

  /**
   * Live particle count, exposed so emitters (e.g. the flamethrower) can back
   * off when the shared pool is under pressure.
   * @returns {number}
   */
  get liveCount() {
    return this._count;
  }

  /** @returns {void} */
  init() {
    const { config, scene } = this.ctx;
    this._capacity = config.particles.max;

    this._positions = new Float32Array(this._capacity * 3);
    this._velocities = new Float32Array(this._capacity * 3);
    this._life = new Float32Array(this._capacity);
    this._maxLife = new Float32Array(this._capacity);
    this._size = new Float32Array(this._capacity);
    this._colors = new Float32Array(this._capacity * 3);

    const geometry = new THREE.SphereGeometry(0.5, 6, 4);
    const material = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      toneMapped: false,
      blending: THREE.AdditiveBlending
    });

    this._mesh = new THREE.InstancedMesh(geometry, material, this._capacity);
    this._mesh.name = 'Particles';
    this._mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this._mesh.frustumCulled = false;
    this._mesh.count = 0;
    // Per-instance colour lets one material serve every effect.
    this._mesh.instanceColor = new THREE.InstancedBufferAttribute(this._colors, 3);
    this._mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    scene.add(this._mesh);

    const { events, config: cfg } = this.ctx;
    this._subscriptions.push(
      // A weapon may ask for a fatter flash than the global default; the
      // shotgun's wide blast is nothing but a bigger burst.
      events.on(Events.WEAPON_FIRED, ({ position, direction, muzzleCount }) =>
        this._burst(position, direction, muzzleCount || cfg.particles.muzzleCount, {
          speed: [6, 14],
          spread: 0.5,
          life: [0.05, 0.14],
          size: [0.12, 0.3],
          color: PALETTE.muzzle
        })
      ),
      events.on(Events.BULLET_HIT, ({ position, normal }) =>
        this._burst(position, normal, cfg.particles.impactCount, {
          speed: [4, 12],
          spread: 0.85,
          life: [0.12, 0.34],
          size: [0.08, 0.2],
          color: PALETTE.impact
        })
      ),
      // A blade gets an arc, not a cone: the whole point is that a swing must not
      // read as a shot (issue #122).
      events.on(Events.MELEE_SWING, (swing) => this._arc(swing)),

      events.on(Events.DIED, ({ entity }) => this._onDeath(entity)),

      // Generic request channel: any system can ask for particles without
      // importing this one.
      events.on(Events.FX_BURST, ({ position, direction, count, options }) =>
        this._burst(position, direction, count, options)
      ),

      events.on(Events.SPAWN_TELEGRAPH, ({ position, duration = WAVES.telegraphTime }) =>
        this._ring(position, 1.6, cfg.particles.telegraphCount, {
          life: [duration, duration],
          size: [0.16, 0.24],
          color: PALETTE.telegraph,
          rise: 2.4
        })
      ),

      events.on(Events.PICKUP_COLLECTED, ({ definition, entity }) => {
        const transform = entity.get(Transform);
        if (!transform) return;
        this._ring(transform.position, 0.9, 16, {
          life: [0.3, 0.45],
          size: [0.1, 0.2],
          color: definition.color,
          rise: 4.5
        });
      })
    );
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Entity that died.
   * @returns {void}
   * @private
   */
  _onDeath(entity) {
    const transform = entity.get(Transform);
    if (!transform) return;

    // A breaking prop throws earthy debris rather than an enemy's death shatter.
    const destructible = entity.get(Destructible);
    if (destructible) {
      this._position.set(0, 1, 0);
      this._burst(transform.position, this._position, destructible.debrisCount, {
        speed: [2, 10],
        spread: 1,
        life: [0.35, 0.9],
        size: [0.1, 0.3],
        color: destructible.type === 'barrel' ? PALETTE.explosiveBarrel : 0x9c7b45,
        yOffset: 0.5
      });
      return;
    }

    // Colour comes from the archetype so every enemy shatters in its own hue.
    const archetype = ENEMIES[entity.archetypeKey];
    const color = archetype ? archetype.color : PALETTE.enemyGrunt;

    this._position.set(0, 1, 0);
    this._burst(transform.position, this._position, this.ctx.config.particles.deathCount, {
      speed: [3, 13],
      spread: 1,
      life: [0.3, 0.75],
      size: [0.14, 0.34],
      color,
      yOffset: 0.8
    });
  }

  /**
   * Emits particles evenly around a circle, rising slowly. Used for spawn
   * warnings and pickup pops, where a directional cone would read as an impact.
   *
   * @param {THREE.Vector3} origin Circle centre.
   * @param {number} radius Circle radius.
   * @param {number} count Particles to emit.
   * @param {object} options Emission shape.
   * @param {[number, number]} options.life Lifetime range in seconds.
   * @param {[number, number]} options.size Radius range.
   * @param {number} options.color Hex colour.
   * @param {number} options.rise Upward speed.
   * @returns {void}
   * @private
   */
  _ring(origin, radius, count, options) {
    this._color.setHex(options.color);

    for (let i = 0; i < count; i++) {
      if (this._count >= this._capacity) return;

      const index = this._count++;
      const p3 = index * 3;
      const angle = (i / count) * Math.PI * 2;

      this._positions[p3] = origin.x + Math.cos(angle) * radius;
      this._positions[p3 + 1] = origin.y + 0.15;
      this._positions[p3 + 2] = origin.z + Math.sin(angle) * radius;

      this._velocities[p3] = Math.cos(angle) * 0.6;
      this._velocities[p3 + 1] = options.rise;
      this._velocities[p3 + 2] = Math.sin(angle) * 0.6;

      const life = randomRange(options.life[0], options.life[1]);
      this._life[index] = life;
      this._maxLife[index] = life;
      this._size[index] = randomRange(options.size[0], options.size[1]);

      this._colors[p3] = this._color.r;
      this._colors[p3 + 1] = this._color.g;
      this._colors[p3 + 2] = this._color.b;
    }
  }

  /**
   * Lays a short trail of particles along a swing arc, ahead of the swinger and
   * centred on the aim. Each one is placed a little further round the arc and
   * given a little more life than the last, so the arc reads as swept rather than
   * as appearing all at once — that ordering is the whole trail effect, and it
   * costs nothing but the loop it is already in.
   *
   * @param {{ position: { x: number, y: number, z: number },
   *           direction: { x: number, z: number }, range: number,
   *           arcDegrees: number, duration: number, height: number,
   *           color: number }} swing Swing to draw.
   * @returns {void}
   * @private
   */
  _arc(swing) {
    const { position, direction, range, arcDegrees, duration, height, color } = swing;
    if (!position || range <= 0) return;

    const count = this.ctx.config.particles.meleeArcCount;
    const aim = Math.atan2(direction.x, direction.z);
    const half = ((arcDegrees || 120) * Math.PI) / 360;
    const radius = range * 0.72;
    this._color.setHex(color);

    for (let i = 0; i < count; i++) {
      if (this._count >= this._capacity) return;

      const index = this._count++;
      const p3 = index * 3;
      // -half .. +half, so the blade passes through the aim direction halfway.
      const t = count > 1 ? i / (count - 1) : 0.5;
      const angle = aim - half + 2 * half * t;
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);

      this._positions[p3] = position.x + sin * radius;
      this._positions[p3 + 1] = height;
      this._positions[p3 + 2] = position.z + cos * radius;

      // Drifting outward along the arc's own normal keeps the trail from
      // collapsing into a dotted line as it fades.
      this._velocities[p3] = sin * 1.6;
      this._velocities[p3 + 1] = 0.4;
      this._velocities[p3 + 2] = cos * 1.6;

      const life = duration * (0.45 + 0.55 * t);
      this._life[index] = life;
      this._maxLife[index] = life;
      this._size[index] = 0.1 + 0.07 * Math.sin(Math.PI * t);

      this._colors[p3] = this._color.r;
      this._colors[p3 + 1] = this._color.g;
      this._colors[p3 + 2] = this._color.b;
    }
  }

  /**
   * Emits a cone of particles.
   *
   * @param {THREE.Vector3} origin Emission point.
   * @param {THREE.Vector3} direction Cone axis; need not be normalised.
   * @param {number} count How many particles to emit.
   * @param {object} options Emission shape.
   * @param {[number, number]} options.speed Speed range.
   * @param {number} options.spread Cone half-width, 0 = laser, 1 = sphere.
   * @param {[number, number]} options.life Lifetime range in seconds.
   * @param {[number, number]} options.size Radius range.
   * @param {number} options.color Hex colour.
   * @param {number} [options.yOffset] Height added to the origin.
   * @returns {void}
   * @private
   */
  _burst(origin, direction, count, options) {
    this._color.setHex(options.color);
    const yOffset = options.yOffset ?? 0;

    for (let i = 0; i < count; i++) {
      if (this._count >= this._capacity) return;

      const index = this._count++;
      const p3 = index * 3;

      this._positions[p3] = origin.x;
      this._positions[p3 + 1] = origin.y + yOffset;
      this._positions[p3 + 2] = origin.z;

      const speed = randomRange(options.speed[0], options.speed[1]);
      const spread = options.spread;
      this._velocities[p3] = (direction.x + randomRange(-spread, spread)) * speed;
      this._velocities[p3 + 1] = (direction.y + randomRange(-spread, spread) + 0.35) * speed;
      this._velocities[p3 + 2] = (direction.z + randomRange(-spread, spread)) * speed;

      const life = randomRange(options.life[0], options.life[1]);
      this._life[index] = life;
      this._maxLife[index] = life;
      this._size[index] = randomRange(options.size[0], options.size[1]);

      this._colors[p3] = this._color.r;
      this._colors[p3 + 1] = this._color.g;
      this._colors[p3 + 2] = this._color.b;
    }
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const { gravity, drag } = this.ctx.config.particles;
    const damping = Math.max(0, 1 - drag * dt);

    for (let index = 0; index < this._count; ) {
      this._life[index] -= dt;
      if (this._life[index] <= 0) {
        this._removeAt(index);
        continue;
      }

      const p3 = index * 3;
      this._velocities[p3] *= damping;
      this._velocities[p3 + 1] = this._velocities[p3 + 1] * damping + gravity * dt;
      this._velocities[p3 + 2] *= damping;

      this._positions[p3] += this._velocities[p3] * dt;
      this._positions[p3 + 1] += this._velocities[p3 + 1] * dt;
      this._positions[p3 + 2] += this._velocities[p3 + 2] * dt;

      // Bounce off the floor instead of sinking through it.
      if (this._positions[p3 + 1] < 0.05) {
        this._positions[p3 + 1] = 0.05;
        this._velocities[p3 + 1] *= -0.35;
      }

      // Particles shrink as they age, which reads as a fade with no per-instance alpha.
      const fade = this._life[index] / this._maxLife[index];
      const scale = this._size[index] * fade;

      this._position.set(this._positions[p3], this._positions[p3 + 1], this._positions[p3 + 2]);
      this._scale.set(scale, scale, scale);
      this._matrix.compose(this._position, this._quaternion, this._scale);
      this._mesh.setMatrixAt(index, this._matrix);

      index++;
    }

    this._mesh.count = this._count;
    this._mesh.instanceMatrix.needsUpdate = true;
    this._mesh.instanceColor.needsUpdate = true;
  }

  /**
   * Swap-remove keeps the live particles contiguous, which is what lets the
   * instance count double as the draw count.
   * @param {number} index Slot to free.
   * @returns {void}
   * @private
   */
  _removeAt(index) {
    const last = --this._count;
    if (index === last) return;

    const to = index * 3;
    const from = last * 3;
    for (let axis = 0; axis < 3; axis++) {
      this._positions[to + axis] = this._positions[from + axis];
      this._velocities[to + axis] = this._velocities[from + axis];
      this._colors[to + axis] = this._colors[from + axis];
    }
    this._life[index] = this._life[last];
    this._maxLife[index] = this._maxLife[last];
    this._size[index] = this._size[last];
  }

  /**
   * Drops every live particle without touching the buffers.
   * @returns {void}
   */
  clear() {
    this._count = 0;
    if (this._mesh) this._mesh.count = 0;
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    if (!this._mesh) return;
    this._mesh.removeFromParent();
    this._mesh.geometry.dispose();
    /** @type {THREE.Material} */ (this._mesh.material).dispose();
    this._mesh = null;
  }
}
